import { useEffect, useState } from 'react'
import { UserTypes } from 'store/auth/types'
import { useGetFeedbackWidgetBySlugQuery } from 'store/queries/bolt-api/feedback-widgets'
import { useStoreSelector } from './store'
import { UseOpenFeedbackWidget } from 'hooks/types.ts'
import { WidgetSlug } from 'types/feedbackWidget.ts'
import { hasDismissedFeedbackWidgets } from 'components/feedback-widget/utils.ts'

/**
 * Custom hook to trigger the feedback widget after x seconds, unless the user dismissed a feedback widget in the last 30 days
 *
 * @param {UseOpenFeedbackWidget} params
 */
const useOpenFeedbackWidget = ({ delay, slug, startTimer = true, startOpen, skip }: UseOpenFeedbackWidget) => {
  // Redux
  const { show } = useStoreSelector((state) => state.app.salesSurvey)
  const { userType } = useStoreSelector((store) => store.auth)

  // Constants
  const salesSurveyWidgetIsVisible = show === 'intro'
  const isSales = userType === UserTypes.SALES
  const isSuperUser = userType === UserTypes.SUPER_USER
  const isFixedWidget = slug === WidgetSlug.BETA_NEW_PORTAL

  // Redux query
  const { data: feedbackWidgetData } = useGetFeedbackWidgetBySlugQuery({ slug }, { skip: skip || userType !== UserTypes.CUSTOMER })
  const { data: fixedFeedbackWidgetData } = useGetFeedbackWidgetBySlugQuery(
    { slug: WidgetSlug.BETA_NEW_PORTAL },
    { skip: skip || isFixedWidget || userType !== UserTypes.CUSTOMER }
  )

  const isDismissed = !isFixedWidget && hasDismissedFeedbackWidgets(fixedFeedbackWidgetData?._id)
  const canOpen = !skip && !salesSurveyWidgetIsVisible && !isDismissed

  // Local state
  const [openFeedbackWidget, setOpenFeedbackWidget] = useState<boolean>(canOpen && !!startOpen)

  // Open feedback widget after x seconds
  useEffect(() => {
    if (canOpen && typeof delay === 'number' && startTimer && !isSales && !isSuperUser) {
      const timeout = setTimeout(() => {
        setOpenFeedbackWidget(true)
      }, delay * 1000)

      return () => clearTimeout(timeout)
    }
    return
  }, [canOpen, isSales, isSuperUser, delay, startTimer])

  return { openFeedbackWidget, feedbackWidgetData }
}

export default useOpenFeedbackWidget
